// app/javascript/widget/cart.ts — подтверждённый заказ в корзине САЙТА.
//
// Чистая логика блока `message.cart` (Orders::Cart): проверка позиций,
// «класть ли ещё раз», поля запроса для «своего сайта» и данные для формы
// заказа. DOM и fetch здесь нет намеренно — модуль тестируется в изоляции
// (cart.test.mjs), как shop.ts.
import type { CartItem, ContactInfo, ShopConfig } from './api';
import type { CheckoutData } from './adapters';
import { cartFields } from './shop.ts';

/** Потолки — зеркало серверных Orders::Compose::MAX_ITEMS / MAX_QUANTITY:
 *  сколько бы ни приехало, в корзину уходит конечное. */
export const CART_MAX_ITEMS = 20;
export const CART_MAX_QUANTITY = 100;

/** Позиции к отправке: без идентификатора класть нечем, без названия —
 *  нечего показать в списке; количество — целое в границах. */
export function cartItems(raw: unknown): CartItem[] {
  if (!Array.isArray(raw)) return [];
  const items: CartItem[] = [];
  raw.forEach((row) => {
    if (typeof row !== 'object' || row === null) return;
    const { id, quantity, title } = row as { id?: unknown; quantity?: unknown; title?: unknown };
    const key = typeof id === 'string' ? id.trim() : typeof id === 'number' ? String(id) : '';
    const name = typeof title === 'string' ? title.trim() : '';
    if (!key || !name) return;
    const count = Number(quantity);
    const safe = Number.isInteger(count) ? Math.min(Math.max(count, 1), CART_MAX_QUANTITY) : 1;
    items.push({ id: key, quantity: safe, title: name });
  });
  return items.slice(0, CART_MAX_ITEMS);
}

/** Ключ localStorage: id последнего сообщения, чью корзину уже положили.
 *  По посетителю, а не по виджету: тот же гость на другой вкладке — тот же
 *  разговор и та же корзина сайта. */
export function cartStorageKey(visitorKey: string): string {
  return `assiistent_cart_synced_${visitorKey}`;
}

/** Класть ли: только сообщение НОВЕЕ уже положенного. Поллинг перечитывает
 *  ленту, страница перезагружается, история рисуется заново — а в корзину
 *  состав должен уехать ровно один раз. */
export function needsCartSync(messageId: number, syncedId: number): boolean {
  return Number.isFinite(messageId) && messageId > (Number.isFinite(syncedId) ? syncedId : 0);
}

/** Что уже положили: id последнего сообщения с корзиной и состав по позициям
 *  (id товара → количество). Состав нужен, потому что гость правит заказ и
 *  подтверждает его снова: движок, который ПРИБАВЛЯЕТ, получил бы прежние
 *  позиции второй раз (замечание ревью 05.09.2026). */
export interface CartState {
  id: number;
  items: Record<string, number>;
}

const EMPTY_STATE: CartState = { id: 0, items: {} };

/** Состояние из localStorage: JSON нового формата или голый id прежнего;
 *  мусор читается как «ничего не клали». */
export function readCartState(raw: string | null | undefined): CartState {
  if (!raw) return EMPTY_STATE;
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && raw.trim() !== '') return { id: numeric, items: {} };
  try {
    const parsed = JSON.parse(raw) as { id?: unknown; items?: unknown };
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_STATE;
    const items: Record<string, number> = {};
    const source = typeof parsed.items === 'object' && parsed.items !== null ? parsed.items : {};
    Object.entries(source as Record<string, unknown>).forEach(([key, qty]) => {
      const count = Number(qty);
      if (key && Number.isInteger(count) && count > 0) items[key] = count;
    });
    return { id: Number(parsed.id) || 0, items };
  } catch {
    return EMPTY_STATE;
  }
}

/** Состояние — в строку для localStorage. */
export function writeCartState(state: CartState): string {
  return JSON.stringify({ id: state.id, items: state.items });
}

/** План отправки: сколько слать по каждой позиции. Движок выставляет
 *  количество (setsQuantity) — абсолютное число всегда; движок прибавляет —
 *  только прирост к уже положенному, а 0 значит «позиция уже в корзине в
 *  нужном количестве или больше» (убавить чужую корзину мы не беремся). */
export function cartPlan(
  items: CartItem[],
  state: CartState,
  setsQuantity: boolean,
): { item: CartItem; send: number }[] {
  return items.map((item) => {
    const had = state.items[item.id] ?? 0;
    const send = setsQuantity ? item.quantity : Math.max(item.quantity - had, 0);
    return { item, send };
  });
}

/** Состояние после того, как позиция легла в корзину в нужном количестве. */
export function cartStateWith(state: CartState, item: CartItem): CartState {
  return { id: state.id, items: { ...state.items, [item.id]: item.quantity } };
}

/** Поля запроса к корзине «своего сайта» для позиции заказа: поля карточки
 *  (id/тип) плюс количество, если владелец назвал поле. Без поля количества
 *  положить можно только одну штуку — «×3» тогда честно не кладём (null),
 *  а не кладём одну молча. */
export function orderCartFields(
  item: CartItem,
  config: ShopConfig | null | undefined,
): Record<string, string> | null {
  const fields = cartFields({ id: item.id, title: item.title, image: '' }, config);
  if (!fields) return null;
  if (config?.qty_field) return { ...fields, [config.qty_field]: String(item.quantity) };
  return item.quantity === 1 ? fields : null;
}

/** Что подставить в форму заказа: контакты из разговора и, если есть куда,
 *  короткая пометка о составе — менеджеру видно, что заказ собран в чате. */
export function checkoutData(
  contact: ContactInfo | null | undefined,
  items: CartItem[],
  label: string,
): CheckoutData {
  const data: CheckoutData = {};
  if (contact?.name) data.name = contact.name;
  if (contact?.phone) data.phone = contact.phone;
  if (contact?.email) data.email = contact.email;
  if (items.length > 0 && label) {
    data.comment = `${label}: ${items.map((item) => `${item.title} × ${item.quantity}`).join('; ')}`;
  }
  return data;
}
